import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Loader2,
  RefreshCw,
  Upload
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  createClosedDealAttachment,
  downloadClosedDealAttachment,
  getClosedDealKycStatus,
  reverifyClosedDealAttachmentKyc,
  uploadFilesToS3
} from '../../services/api/implementation/impl';
import {
  evaluateKycCompleteness,
  evaluateKycVerification,
  kycVerifyBadgeClasses,
  KYC_CATEGORIES,
  KYC_CATEGORY_LABELS,
  matchStatusLabel,
  normalizeKycCategory,
  pickLatestKycAttachment,
  SECONDARY_ID_OPTIONS
} from './postSalesKyc';

const SLOTS = [
  {
    category: KYC_CATEGORIES.PAN,
    label: KYC_CATEGORY_LABELS.pan,
    required: true,
    hint: 'Required for every buyer — number must match Application PAN'
  }
];

const DealKycDocumentsSection = ({
  dealId,
  attachments = [],
  onUpdated,
  readOnly = false
}) => {
  const [secondaryChoice, setSecondaryChoice] = useState(KYC_CATEGORIES.AADHAAR);
  const [uploadingSlot, setUploadingSlot] = useState(null);
  const fileRefs = useRef({});

  const kyc = evaluateKycCompleteness(attachments);

  const {
    data: kycStatusRaw,
    refetch: refetchKycStatus,
    isFetching: kycStatusFetching
  } = useQuery({
    queryKey: ['closedDealKycStatus', dealId],
    queryFn: () => getClosedDealKycStatus(dealId),
    enabled: Boolean(dealId),
    refetchInterval: (query) => {
      const status = query.state.data?.kyc_verify_status;
      return status === 'extracting' ? 3000 : false;
    }
  });

  const verification = useMemo(
    () => evaluateKycVerification(kycStatusRaw),
    [kycStatusRaw]
  );

  useEffect(() => {
    if (kyc.hasAadhaar && !kyc.hasVoter) {
      setSecondaryChoice(KYC_CATEGORIES.AADHAAR);
    } else if (kyc.hasVoter && !kyc.hasAadhaar) {
      setSecondaryChoice(KYC_CATEGORIES.VOTER_ID);
    }
  }, [kyc.hasAadhaar, kyc.hasVoter]);

  // Re-uploads pile up as extra rows; only the newest one is shown/verified.
  const latestByCategory = (cat) =>
    pickLatestKycAttachment(attachments.filter((a) => normalizeKycCategory(a) === cat));

  // Same rule as the backend roll-up: the newest document of the slot wins.
  const docMatchByCategory = (cat) =>
    pickLatestKycAttachment(
      (verification.documents || []).filter(
        (d) => String(d.category).toLowerCase() === cat
      )
    );

  const uploadMutation = useMutation({
    mutationFn: async ({ file, category, label }) => {
      setUploadingSlot(category);
      const uploaded = await uploadFilesToS3([file]);
      if (!uploaded?.length) throw new Error('Upload failed');
      const fileMeta = uploaded[0];
      return createClosedDealAttachment({
        deal_id: dealId,
        name: label,
        url: fileMeta.url,
        mime_type: file.type,
        size_bytes: file.size,
        category
      });
    },
    onSuccess: () => {
      toast.success('Document uploaded — verifying against Application…');
      onUpdated?.();
      refetchKycStatus();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Upload failed');
    },
    onSettled: () => setUploadingSlot(null)
  });

  const downloadMutation = useMutation({
    mutationFn: (id) => downloadClosedDealAttachment(id),
    onSuccess: ({ data, filename, contentType }) => {
      const blob = new Blob([data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'kyc-document';
      a.click();
      window.URL.revokeObjectURL(url);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Download failed');
    }
  });

  const reverifyMutation = useMutation({
    mutationFn: (id) => reverifyClosedDealAttachmentKyc(id),
    onSuccess: () => {
      toast.success('Re-verification finished');
      onUpdated?.();
      refetchKycStatus();
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || err?.message || 'Reverify failed');
    }
  });

  const pickFile = (category, label) => {
    if (readOnly) return;
    const input = fileRefs.current[category];
    if (!input) return;
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) uploadMutation.mutate({ file, category, label });
      e.target.value = '';
    };
    input.click();
  };

  const secondaryCategory = secondaryChoice;
  const secondaryLabel =
    SECONDARY_ID_OPTIONS.find((o) => o.id === secondaryCategory)?.label || 'ID proof';

  const missingApplicantHint =
    !verification.applicant?.has_pan || !verification.applicant?.has_aadhar
      ? 'Fill primary applicant PAN & Aadhaar in Applicants before verification can succeed.'
      : null;

  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 sm:px-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">KYC documents</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            PAN is required. Upload Aadhaar for number verification (Voter ID counts as
            uploaded but not as verified).
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
              kyc.complete
                ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
                : 'bg-amber-50 text-amber-900 ring-1 ring-amber-200'
            }`}>
            {kyc.complete ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5" />
            )}
            {kyc.complete ? 'Docs uploaded' : `Missing: ${kyc.missing.join(', ')}`}
          </div>
          <div
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${kycVerifyBadgeClasses(
              verification.status
            )}`}>
            {verification.extracting || kycStatusFetching ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : verification.verified ? (
              <CheckCircle2 className="h-3.5 w-3.5" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5" />
            )}
            {verification.label}
          </div>
        </div>
      </div>

      {missingApplicantHint && (
        <div className="mx-4 mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 sm:mx-5">
          {missingApplicantHint}
        </div>
      )}

      <div className="space-y-3 p-4 sm:p-5">
        {SLOTS.map((slot) => (
          <KycSlotRow
            key={slot.category}
            slot={slot}
            file={latestByCategory(slot.category)}
            matchDoc={docMatchByCategory(slot.category)}
            uploading={uploadingSlot === slot.category}
            readOnly={readOnly}
            reverifyPending={reverifyMutation.isPending}
            onUpload={() => pickFile(slot.category, slot.label)}
            onDownload={(id) => downloadMutation.mutate(id)}
            onReverify={(id) => reverifyMutation.mutate(id)}
            inputRef={(el) => {
              fileRefs.current[slot.category] = el;
            }}
          />
        ))}

        <div className="rounded-xl border border-gray-200 bg-gray-50/50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
            Second ID proof <span className="text-red-600">*</span>
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Choose Aadhaar for auto number match. Voter ID is accepted for upload completeness
            only.
          </p>
          {!readOnly && (
            <div className="mt-3 flex flex-wrap gap-3">
              {SECONDARY_ID_OPTIONS.map((opt) => (
                <label
                  key={opt.id}
                  className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm">
                  <input
                    type="radio"
                    name="secondary-id"
                    checked={secondaryChoice === opt.id}
                    onChange={() => setSecondaryChoice(opt.id)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          )}
          <div className="mt-3">
            <KycSlotRow
              slot={{
                category: secondaryCategory,
                label: secondaryLabel,
                required: true,
                hint: `Upload ${secondaryLabel}`
              }}
              file={latestByCategory(secondaryCategory)}
              matchDoc={docMatchByCategory(secondaryCategory)}
              uploading={uploadingSlot === secondaryCategory}
              readOnly={readOnly}
              reverifyPending={reverifyMutation.isPending}
              onUpload={() => pickFile(secondaryCategory, secondaryLabel)}
              onDownload={(id) => downloadMutation.mutate(id)}
              onReverify={(id) => reverifyMutation.mutate(id)}
              inputRef={(el) => {
                fileRefs.current[secondaryCategory] = el;
              }}
              compact
            />
          </div>
        </div>
      </div>
    </section>
  );
};

function KycSlotRow({
  slot,
  file,
  matchDoc,
  uploading,
  readOnly,
  reverifyPending,
  onUpload,
  onDownload,
  onReverify,
  inputRef,
  compact = false
}) {
  const hasFile = Boolean(file);
  const matchInfo = matchStatusLabel(matchDoc?.kyc_match_status);
  const canReverify =
    !readOnly &&
    hasFile &&
    (slot.category === KYC_CATEGORIES.PAN || slot.category === KYC_CATEGORIES.AADHAAR);

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 ${
        compact ? 'bg-white p-3' : 'bg-gray-50/40 p-4'
      }`}>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">
          {slot.label}
          {slot.required && <span className="ml-1 text-red-500">*</span>}
        </p>
        {!compact && <p className="text-xs text-gray-500">{slot.hint}</p>}
        {hasFile && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-600">
            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
            <span className="truncate">{file.name}</span>
            {matchDoc && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                  matchInfo.tone === 'emerald'
                    ? 'bg-emerald-50 text-emerald-800'
                    : matchInfo.tone === 'rose'
                      ? 'bg-rose-50 text-rose-800'
                      : matchInfo.tone === 'amber'
                        ? 'bg-amber-50 text-amber-900'
                        : 'bg-gray-100 text-gray-700'
                }`}>
                {matchInfo.label}
                {matchDoc.extracted_last4 ? ` · …${matchDoc.extracted_last4}` : ''}
              </span>
            )}
            <button
              type="button"
              onClick={() => onDownload(file.id)}
              className="text-teal-700 hover:underline">
              <Download className="inline h-3 w-3" />
            </button>
            {canReverify && (
              <button
                type="button"
                disabled={reverifyPending}
                onClick={() => onReverify(file.id)}
                className="inline-flex items-center gap-1 text-gray-600 hover:text-teal-800 disabled:opacity-50"
                title="Re-run verification">
                <RefreshCw className="h-3 w-3" />
                Reverify
              </button>
            )}
          </div>
        )}
      </div>
      {!readOnly && (
        <button
          type="button"
          disabled={uploading}
          onClick={onUpload}
          className="inline-flex items-center gap-1.5 rounded-lg border border-[#2A565E]/30 bg-white px-3 py-2 text-xs font-semibold text-[#2A565E] hover:bg-teal-50 disabled:opacity-50">
          {uploading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Upload className="h-3.5 w-3.5" />
          )}
          {hasFile ? 'Replace' : 'Upload'}
        </button>
      )}
    </div>
  );
}

export default DealKycDocumentsSection;
